import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Filter, MapPin, Zap, ArrowRight } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { CTABanner } from '../components/shared/CTABanner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const industries = [
  { value: 'all', label: 'All Industries' },
  { value: 'fleet', label: 'Fleet & Logistics' },
  { value: 'commercial', label: 'Commercial & Retail' },
  { value: 'municipal', label: 'Utilities & Municipalities' }
];

export const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedIndustry === 'all') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(p => p.industry === selectedIndustry));
    }
  }, [selectedIndustry, projects]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      setFilteredProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="pt-20" data-testid="projects-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-slate-50 circuit-pattern" data-testid="projects-hero">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2B5] mb-4 block">
              Our Work
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 mb-6">
              Projects &{' '}
              <span className="gradient-text">Case Studies</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Explore our portfolio of successful EV infrastructure deployments across fleets, commercial properties, and municipal installations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter & Projects Grid */}
      <section className="py-20 md:py-28 bg-white" data-testid="projects-grid">
        <div className="container-custom">
          {/* Filter */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-12">
            <div className="flex items-center gap-2 text-slate-600">
              <Filter className="w-5 h-5" />
              <span className="font-medium">{filteredProjects.length} Projects</span>
            </div>
            
            <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
              <SelectTrigger className="w-[200px]" data-testid="industry-filter">
                <SelectValue placeholder="Filter by industry" />
              </SelectTrigger>
              <SelectContent>
                {industries.map((industry) => (
                  <SelectItem key={industry.value} value={industry.value}>
                    {industry.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Projects Grid */}
          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-slate-100 rounded-xl h-96 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-xl overflow-hidden border border-slate-200 card-hover cursor-pointer"
                  onClick={() => setSelectedProject(project)}
                  data-testid={`project-card-${project.id}`}
                >
                  <div className="relative h-52 overflow-hidden">
                    <img 
                      src={project.image_url} 
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-white/90 rounded-full text-xs font-semibold text-slate-700">
                        <MapPin className="w-3 h-3" />
                        {project.location}
                      </span>
                    </div>
                    {project.featured && (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 bg-gradient-to-r from-[#00A2B5] to-[#36D890] rounded-full text-xs font-bold text-white">
                          Featured
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#00A2B5] mb-2">
                      {project.client_type}
                    </p>
                    <h3 className="text-lg font-bold text-slate-900 mb-3">{project.title}</h3>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">{project.scope}</p>
                    
                    {/* Metrics */}
                    <div className="flex items-center gap-4 mb-4">
                      {project.metrics.chargers && (
                        <div className="flex items-center gap-1.5">
                          <Zap className="w-4 h-4 text-[#00A2B5]" />
                          <span className="text-sm font-semibold text-slate-700">{project.metrics.chargers} Chargers</span>
                        </div>
                      )}
                      {project.metrics.uptime && (
                        <div className="px-2 py-1 bg-green-50 rounded-full">
                          <span className="text-xs font-semibold text-green-700">{project.metrics.uptime} Uptime</span>
                        </div>
                      )}
                    </div>

                    <p className="text-sm text-slate-900 font-semibold">{project.result}</p>
                    
                    <button 
                      className="inline-flex items-center text-[#00A2B5] font-semibold text-sm mt-4 group"
                      data-testid={`view-details-${project.id}`}
                    >
                      View Details
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {filteredProjects.length === 0 && !isLoading && (
            <div className="text-center py-20">
              <p className="text-slate-500 text-lg">No projects found for this filter.</p>
            </div>
          )}
        </div>
      </section>

      {/* Project Detail Modal */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="project-modal">
          {selectedProject && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-black text-slate-900">
                  {selectedProject.title}
                </DialogTitle>
                <DialogDescription className="text-[#00A2B5] font-semibold">
                  {selectedProject.client_type} • {selectedProject.location}
                </DialogDescription>
              </DialogHeader>

              <div className="mt-4">
                <div className="rounded-xl overflow-hidden mb-6">
                  <img 
                    src={selectedProject.image_url} 
                    alt={selectedProject.title}
                    className="w-full h-64 object-cover"
                  />
                </div>

                <div className="space-y-6">
                  <div>
                    <h4 className="font-bold text-slate-900 mb-2">Project Scope</h4>
                    <p className="text-slate-600">{selectedProject.scope}</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-slate-900 mb-2">Results</h4>
                    <p className="text-slate-600">{selectedProject.result}</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-slate-900 mb-3">Key Metrics</h4>
                    <div className="grid grid-cols-3 gap-4">
                      {Object.entries(selectedProject.metrics).map(([key, value]) => (
                        <div key={key} className="bg-slate-50 rounded-xl p-4 text-center border border-slate-200">
                          <p className="text-2xl font-black gradient-text mb-1">{value}</p>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">{key.replace('_', ' ')}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* CTA Banner */}
      <CTABanner 
        title="Start Your Project"
        subtitle="Ready to deploy EV infrastructure like these? Let's discuss your requirements."
        primaryCTA="Request a Consultation"
      />
    </main>
  );
};

export default ProjectsPage;
